/**
 * Pure PostHog init config for the dashboard.
 *
 * Extracted from analytics.ts so the options (including exception autocapture)
 * are unit-testable without touching import.meta.env or posthog-js. analytics.ts
 * owns the runtime concerns (key, localhost guard, internal-user opt-out).
 */
import { dropScannerNoiseExceptions, dropWhenScannerSession } from './scannerNoise';

/**
 * First-party proxy on our own origin (vercel.json rewrites /oqi to PostHog;
 * pinned by lib/__tests__/posthogProxy.source.test.ts). Events sent straight to
 * us.i.posthog.com are dropped by ad blockers, so a signed-in user behind one
 * was invisible to every funnel (PostHog health check, scout report
 * 01a044bc-c3f6, 2026-09-03). The path is deliberately not an obvious word.
 * The EXTENSION keeps the direct host: its background worker is not blocked
 * the same way and cannot route through the app origin.
 */
export const POSTHOG_HOST = 'https://app.overlayqa.com/oqi';
/** Where the toolbar and app links go; the proxy only carries ingestion. */
export const POSTHOG_UI_HOST = 'https://us.posthog.com';

/**
 * before_send filter: drop raw $autocapture click events, keep everything
 * else. autocapture must be ON for posthog-js to detect $rageclick (the
 * detection lives inside the autocapture module, whose isEnabled gate
 * requires config.autocapture truthy; verified in posthog-js 1.369.3
 * source), but we do not want the per-click $autocapture event volume.
 * Dropping client-side means those clicks never leave the browser.
 */
export function dropRawAutocaptureEvents<T extends { event: string }>(event: T | null): T | null {
  if (!event) return null;
  return event.event === '$autocapture' ? null : event;
}

/**
 * Any absolute http(s) URL, wherever it appears in a string. Deliberately
 * matches URLs EMBEDDED in a larger string, not just whole-string values:
 * `$elements_chain` carries the clicked link as `a:attr__href="https://…"`.
 * Stops at whitespace, quotes and angle brackets, which bound a URL in every
 * property posthog-js builds.
 */
const EMBEDDED_URL = /https?:\/\/[^\s"'<>\\)]+/g;

/**
 * Nested property bags are shallow ($elements is an array of flat objects), so
 * this only has to be deep enough for the real shapes. It also bounds the walk
 * against a pathological or cyclic structure.
 */
const MAX_SCRUB_DEPTH = 6;

/** Reduce one URL to origin + path, dropping the query string AND the fragment. */
function toOriginAndPath(url: string): string {
  try {
    const parsed = new URL(url);
    // `origin` deliberately excludes any user:password@ prefix as well.
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    // A malformed link may still contain credentials. Preserve its surrounding
    // message/selector without forwarding an address we cannot scrub.
    return '[invalid URL]';
  }
}

function scrubValue(value: unknown, depth: number): unknown {
  if (depth > MAX_SCRUB_DEPTH) return value;
  if (typeof value === 'string') {
    // Non-URL strings ($referrer is the literal "$direct" on a direct visit,
    // $referring_domain is a bare host) match nothing and pass through.
    return value.replace(EMBEDDED_URL, toOriginAndPath);
  }
  if (Array.isArray(value)) return value.map((item) => scrubValue(item, depth + 1));
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      out[key] = scrubValue(item, depth + 1);
    }
    return out;
  }
  return value;
}

/**
 * Strip query strings and fragments from every URL reaching PostHog, so a
 * credential sitting in window.location can never be transmitted to, or
 * retained by, a third-party analytics vendor.
 *
 * The dashboard puts secrets on its own URLs: `claim` (a live review bearer
 * token that ALSO authorises claiming a free scan into an account), `s` (the
 * rescan handoff token), `share`, `oqa_ext_anon`, and `email` (raw PII).
 *
 * Verified against posthog-js 1.369.3 rather than assumed — the token escapes
 * through SIX properties, and a key-name match would have caught only three:
 *   $current_url        the page the event fired on
 *   $session_entry_url  session-sticky, so it rides EVERY later event
 *   $referrer           after navigating off the page
 *   attr__href          the clicked link on $dead_click / $rageclick
 *   $elements_chain     the same href, embedded mid-string
 *   $elements           a nested ARRAY of element objects
 * The last three matter because $dead_click and $rageclick are both enabled
 * above, and hesitating on a link is exactly the reported scenario.
 *
 * Same policy the extension has always had (`sanitizeProperties` in
 * packages/extension/src/background/handlers/tracking-allowlist.ts — "scrub
 * query params from URL values to prevent token/PII leakage"). The dashboard was
 * the one surface missing it; keep the two in step.
 *
 * Fail-CLOSED by design: it drops every query string rather than redacting a
 * denylist of known-secret param names, because a name list leaks silently the
 * day a sixth sensitive param is added.
 *
 * Costs no analytics: utm_source, utm_medium, utm_campaign, utm_content,
 * utm_term and gclid are each stamped as their OWN property (plus
 * `$session_entry_*` twins), as are `$pathname` and `$host`, so attribution is
 * unaffected by dropping the query from the URL itself.
 */
export function scrubUrlProperties<T extends { properties?: Record<string, unknown> }>(
  event: T | null,
): T | null {
  if (!event?.properties) return event;
  return { ...event, properties: scrubValue(event.properties, 0) as Record<string, unknown> };
}

export function buildPostHogConfig() {
  return {
    api_host: POSTHOG_HOST,
    ui_host: POSTHOG_UI_HOST,
    person_profiles: 'identified_only' as const,
    // Disabled: we fire $pageview manually after the internal-user check in
    // identify(). Without this, internal users leak anonymous pageviews before
    // identify() runs.
    capture_pageview: false,
    // ON so $rageclick fires (see dropRawAutocaptureEvents above, which strips
    // the raw click events). Frustration signals found real bugs before
    // (free-checker quota burn was spotted via rageclicks); the dashboard was
    // dark for them until 2026-07-14. Known accepted leak: an internal user's
    // rageclick BEFORE identify() opts them out would be captured; identified
    // internal users are opted out as before.
    autocapture: true,
    // $dead_click = click that visibly did nothing: the exact shape of the
    // resurrected no-op "Promote All to Issues" class.
    capture_dead_clicks: true,
    // Composed, in order: strip raw $autocapture volume, then email-link-scanner
    // noise $exceptions (which arms the session latch), then everything else once
    // that latch is armed. The third step is what stops a scanner's downstream
    // ChunkLoadError / Clerk-timeout $exceptions and its sign_in_page_viewed /
    // auth_load_failed product events — none of which carry the signature the
    // middle step matches on. See scannerNoise.ts.
    //
    // scrubUrlProperties runs OUTERMOST on purpose: every event that survives
    // the drop filters is scrubbed, so a later filter cannot be appended after
    // it and let a credential-bearing URL slip out.
    before_send: <T extends { event: string }>(event: T | null) =>
      scrubUrlProperties(
        dropWhenScannerSession(
          dropScannerNoiseExceptions(dropRawAutocaptureEvents(event) as never),
        ),
      ) as T | null,
    // Capture JS + console errors as queryable $exception events / Error Tracking
    // issues. Internal users are opted out in identify(), so this only fires for
    // real users. (A real onboarding session logged 1298 console errors that were
    // invisible to queries because exception autocapture was off.)
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: true,
    },
  };
}
